import { Dialog, Transition } from "@headlessui/react";
import clsx from "clsx";
import { FC, Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Close,
  Globe,
  Logout,
  Mobile,
  PhoneCall,
  Speed,
} from "@/assets/icons/ds";
import {
  ProductTeaser,
  ProductTeaserAccent,
} from "@/components/FriendsWithBenefits/ProductTeaser";
import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import {
  formatStep,
  SlideStepper,
} from "@/components/Interface/Stepper/SlideStepper";
import { NamespaceKey } from "@/utils/translationKey";

import slides from "./slides.json";

const SLIDE_DURATION_MS = 10000;
/** Length of the dimming flash that acknowledges a pause/resume tap. */
const TAP_FEEDBACK_MS = 100;

const slideIcons = {
  mobile: Mobile,
  speed: Speed,
  globe: Globe,
  phone: PhoneCall,
  exit: Logout,
} as const;

const LAST_SLIDE_INDEX = slides.length - 1;

const VALID_ACCENTS = new Set<string>([
  "orange",
  "rose",
] satisfies ProductTeaserAccent[]);
const toAccent = (v: string): ProductTeaserAccent =>
  VALID_ACCENTS.has(v) ? (v as ProductTeaserAccent) : "orange";

type SlideKey = NamespaceKey<"portal", "friendsWithBenefits.welcome.slides">;

/**
 * Numbered slide keys follow slides.json, so they can only be checked at
 * runtime - this is the one place their string is narrowed to a key.
 */
const slideKey = (index: number, field: string) =>
  `slide${index + 1}.${field}` as SlideKey;

/** Bullets whose copy is identical to another slide's read that slide's key. */
const sharedBulletKey: Partial<Record<string, SlideKey>> = {
  "slide4.bullet1": "slide2.bullet1",
};

const bulletKey = (index: number, bulletIndex: number): SlideKey => {
  const key = slideKey(index, `bullet${bulletIndex + 1}`);

  return sharedBulletKey[key] ?? key;
};

/** The last slide keeps its own key set instead of a numbered one. */
const titleKey = (index: number): SlideKey =>
  index === LAST_SLIDE_INDEX ? "lastSlide.title" : slideKey(index, "title");

/** The first slide is titled with the brand name, which has a shared key. */
const FIRST_SLIDE_INDEX = 0;

interface Props {
  onClose: () => void;
  onCTAClick?: () => void;
}

export const SlideShow: FC<Props> = ({ onClose, onCTAClick }) => {
  const { t } = useTranslation(["portal"], {
    keyPrefix: "friendsWithBenefits.welcome.slides",
  });
  const { t: commonT } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [showTapFeedback, setShowTapFeedback] = useState(false);

  const slide = slides[currentSlide];
  const isLastSlide = currentSlide === LAST_SLIDE_INDEX;
  const slideTitle = (index: number) =>
    index === FIRST_SLIDE_INDEX
      ? commonT("Common.logoName")
      : t(titleKey(index));

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setAnimationProgress(0);
    setIsPaused(false);
  };

  const handleTap = () => {
    setShowTapFeedback(true);
    setTimeout(() => {
      setShowTapFeedback(false);
    }, TAP_FEEDBACK_MS);

    setIsPaused(!isPaused);
  };

  useEffect(() => {
    let animationFrame: number;
    let startTime = Date.now() - animationProgress * SLIDE_DURATION_MS;

    const updateProgress = () => {
      const currentTime = Date.now();

      if (isPaused) {
        // Keep the elapsed time frozen so resuming continues where it stopped.
        startTime = currentTime - animationProgress * SLIDE_DURATION_MS;
      } else {
        const progress = Math.min(
          (currentTime - startTime) / SLIDE_DURATION_MS,
          1,
        );
        setAnimationProgress(progress);

        if (progress >= 1) {
          setCurrentSlide((previous) =>
            previous < LAST_SLIDE_INDEX ? previous + 1 : previous,
          );
          startTime = currentTime;
          setAnimationProgress(0);
        }
      }

      animationFrame = requestAnimationFrame(updateProgress);
    };

    animationFrame = requestAnimationFrame(updateProgress);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [currentSlide, isPaused, animationProgress]);

  return (
    <Transition.Root as={Fragment} appear show>
      <Dialog
        data-testid="modal"
        as="div"
        className="relative z-30"
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-10 bg-surface-secondary backdrop-blur-sm transition-opacity" />
        </Transition.Child>
        <div id="modal" className="fixed inset-0 z-40 overflow-y-auto p-3">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 desktop:translate-y-0 desktop:scale-95"
              enterTo="opacity-100 translate-y-0 desktop:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 desktop:scale-100"
              leaveTo="opacity-0 translate-y-4 desktop:translate-y-0 desktop:scale-95"
            >
              <Dialog.Panel className="relative flex w-full max-w-[540px] flex-col gap-8 rounded-lg bg-surface-primary p-6 desktop:p-8">
                <Button
                  className="absolute right-6 top-6 desktop:right-4 desktop:top-4"
                  ariaLabel={commonT("common.buttons.close")}
                  variant="tertiary"
                  inverted
                  icon={<Close />}
                  onClick={onClose}
                />

                <div className="pt-7">
                  <SlideStepper
                    titles={slides.map((_, index) => slideTitle(index))}
                    currentStep={currentSlide}
                    progress={animationProgress}
                    counterLabel={t("counter", {
                      step: formatStep(currentSlide),
                      total: formatStep(LAST_SLIDE_INDEX),
                    })}
                    menuLabel={t("selectSlide")}
                    stepLabel={(step) => t("goToSlide", { step })}
                    onStepChange={goToSlide}
                  />

                  {/* Visible once focused: the design has no pause button, but
                      the auto-advance needs a keyboard-reachable one. */}
                  <Button
                    className="sr-only mt-2 focus:not-sr-only"
                    variant="secondary"
                    size="sm"
                    inverted
                    pressed={isPaused}
                    onClick={() => setIsPaused(!isPaused)}
                  >
                    {isPaused ? t("resumeSlides") : t("pauseSlides")}
                  </Button>
                </div>

                <div
                  className={clsx(
                    "flex flex-col gap-8",
                    showTapFeedback && "opacity-50",
                  )}
                  onClick={handleTap}
                >
                  <div className="hidden desktop:block">
                    <ProductTeaser
                      family={slide.teaser.family}
                      name={slide.teaser.name}
                      accent={toAccent(slide.teaser.accent)}
                    />
                  </div>

                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-6">
                      <Dialog.Title
                        as="h2"
                        className="text-headline-sm font-semibold leading-md text-foreground-primary-inverse"
                      >
                        {isLastSlide
                          ? t("lastSlide.headline")
                          : slideTitle(currentSlide)}
                      </Dialog.Title>

                      {isLastSlide ? (
                        <ol className="flex list-inside list-decimal flex-col gap-3 text-body-base leading-md text-foreground-primary-inverse">
                          <li>
                            {commonT(
                              "portal:friendsWithBenefits.welcome.steps.register",
                            )}
                          </li>
                          <li>{t("lastSlide.steps.invite")}</li>
                          <li>{t("lastSlide.steps.save")}</li>
                        </ol>
                      ) : (
                        <ul className="flex flex-col gap-3">
                          {slide.icons.map((icon, index) => {
                            const Icon =
                              slideIcons[icon as keyof typeof slideIcons];

                            return (
                              <li key={icon} className="flex items-start gap-2">
                                <Icon className="size-6 shrink-0 text-foreground-primary-inverse" />
                                <p className="flex-1 text-body-base leading-md text-foreground-primary-inverse">
                                  {t(bulletKey(currentSlide, index))}
                                </p>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    <Notification
                      inverted
                      title={
                        isLastSlide
                          ? t("lastSlide.notification.title")
                          : t("searchingFor")
                      }
                      description={
                        isLastSlide
                          ? t("lastSlide.notification.description")
                          : t(slideKey(currentSlide, "match"))
                      }
                    />
                  </div>

                  {!isLastSlide && (
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-4">
                        <p className="text-body-base font-semibold leading-none text-foreground-primary-inverse">
                          {t("aboutMe")}
                        </p>
                        <hr className="border-border-tertiary-inverse" />
                      </div>
                      <p className="text-body-base leading-none text-foreground-primary-inverse">
                        {t(slideKey(currentSlide, "description"))}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col-reverse gap-4 desktop:flex-row desktop:justify-end">
                  <Button
                    className="w-full desktop:w-auto"
                    variant="secondary"
                    inverted
                    disabled={currentSlide === 0}
                    onClick={() => goToSlide(currentSlide - 1)}
                  >
                    {commonT("Common.label.back")}
                  </Button>
                  <Button
                    className="w-full desktop:w-auto"
                    variant="primary"
                    inverted
                    onClick={() => {
                      if (isLastSlide) {
                        onCTAClick?.();

                        return;
                      }

                      goToSlide(currentSlide + 1);
                    }}
                  >
                    {isLastSlide
                      ? t("lastSlide.cta")
                      : commonT("common.buttons.continue")}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
